import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PageContainer, SurfaceCard, Table } from '@react-apps/design-system';
import { useTransactions } from '../hooks/use-transactions';
import { ErrorState, getApiErrorMessage } from '../shared/display';
import { buildSalesTransactionColumns } from '../shared/transaction-columns';
import { TablePagination } from '../shared/TablePagination';
import {
  DEFAULT_SALES_SORT,
  EMPTY_SALES_FILTERS,
  SALES_SORT_OPTIONS,
  countActiveSalesFilters,
  readSalesSort,
  toSalesQueryFilters,
  type SalesSort,
  type SalesSortField,
  type SalesTransactionFilters,
} from '../shared/sales-filters';
import { SalesFiltersModal } from '../components/SalesFiltersModal';
import { RefreshButton } from '@/shared/RefreshButton';
import {
  readPageParam,
  writePageParam,
} from '@/shared/hooks/use-page-query-param';
import {
  readFiltersParams,
  writeFiltersParams,
} from '@/shared/hooks/use-url-filters';

const PER_PAGE = 10;

export function SalesPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Página e filtros vivem na URL (não em useState) para sobreviver a
  // "Voltar" do detalhe da transação — ver
  // primum-core-root/docs/plans/done/admin-dashboard-pagination-url-sync.md. Aplicar/limpar filtro
  // também reseta a página; as duas mudanças vão num único setSearchParams
  // (ver aviso em use-page-query-param.ts).
  const page = readPageParam(searchParams);
  const filters = readFiltersParams(searchParams, EMPTY_SALES_FILTERS);
  const setPage = (next: number) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        writePageParam(params, next);
        return params;
      },
      { replace: true },
    );
  };
  const setFiltersAndResetPage = (next: SalesTransactionFilters) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        writeFiltersParams(params, next, EMPTY_SALES_FILTERS);
        writePageParam(params, 1);
        return params;
      },
      { replace: true },
    );
  };
  const sort = readSalesSort(searchParams);
  /**
   * Trocar a ordem volta para a página 1: a página 3 de uma ordem não tem
   * relação com a página 3 da outra (mesmo motivo do `ExamsPanel`).
   */
  const setSortAndResetPage = (next: SalesSort) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        writeFiltersParams(params, next, DEFAULT_SALES_SORT);
        writePageParam(params, 1);
        return params;
      },
      { replace: true },
    );
  };
  /**
   * A direção mora só no cabeçalho da coluna de data, que é sempre a do campo
   * da ordem. O `Table` cicla asc → desc → sem ordem; aqui o clique só alterna.
   */
  const handleHeaderSort = () => {
    setSortAndResetPage({
      ...sort,
      sortDirection: sort.sortDirection === 'desc' ? 'asc' : 'desc',
    });
  };
  const columns = useMemo(
    () => buildSalesTransactionColumns({ sortBy: sort.sortBy }),
    [sort.sortBy],
  );
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  // Todos os filtros vão pro servidor, que devolve o `total` já filtrado — não
  // há mais filtragem local, e a paginação é sobre o conjunto certo.
  const queryFilters = useMemo(
    () => ({
      ...toSalesQueryFilters(filters),
      sortBy: sort.sortBy,
      sortDirection: sort.sortDirection,
    }),
    [filters, sort.sortBy, sort.sortDirection],
  );
  const { data, isLoading, isError, error, refetch, isFetching } =
    useTransactions(page, PER_PAGE, queryFilters);

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PER_PAGE)) : 1;
  const activeFilterCount = countActiveSalesFilters(filters);
  const hasActiveFilter = activeFilterCount > 0;
  const items = data?.items ?? [];

  const handleApplyFilters = (next: SalesTransactionFilters) => {
    setFiltersAndResetPage(next);
  };

  const handleClearFilters = () => {
    setFiltersAndResetPage(EMPTY_SALES_FILTERS);
  };

  return (
    <PageContainer maxWidth="6xl">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-(--color-text-primary) text-2xl font-semibold">
            Vendas
          </h1>
          <p className="text-(--color-text-secondary) mt-1 text-sm">
            Todas as transações da plataforma. Clique em uma transação para ver
            os detalhes.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/*
            Com a cara dos botões ao lado: o `Select` do design system é campo
            de formulário (py-3, largura cheia) e destoava do header. A direção
            não tem controle aqui — fica no cabeçalho da coluna.
          */}
          <label className="border-(--color-border) text-(--color-text-secondary) inline-flex shrink-0 items-center gap-1.5 rounded-lg border pl-3 text-sm font-medium">
            Ordenar por
            <select
              value={sort.sortBy}
              onChange={(e) =>
                setSortAndResetPage({
                  sortBy: e.target.value as SalesSortField,
                  sortDirection: DEFAULT_SALES_SORT.sortDirection,
                })
              }
              className="text-(--color-text-primary) [&>option]:bg-(--color-bg-secondary) cursor-pointer bg-transparent py-2.5 pr-2 outline-none"
            >
              {SALES_SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <RefreshButton
            onRefresh={() => void refetch()}
            isRefreshing={isFetching}
          />
          <button
            type="button"
            onClick={() => setIsFiltersOpen(true)}
            className="border-(--color-border) text-(--color-text-secondary) hover:bg-(--color-bg-secondary) hover:text-(--color-text-primary) rounded-lg border px-5 py-2.5 text-sm font-medium transition"
          >
            Filtros{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
          </button>
        </div>
      </div>

      <SalesFiltersModal
        open={isFiltersOpen}
        onClose={() => setIsFiltersOpen(false)}
        filters={filters}
        onApply={handleApplyFilters}
      />

      <SurfaceCard className="space-y-5">
        {hasActiveFilter && (
          <div className="flex items-center justify-between gap-3 text-xs">
            <p className="text-(--color-text-secondary)">
              {activeFilterCount === 1
                ? '1 filtro aplicado'
                : `${activeFilterCount} filtros aplicados`}
              .
            </p>
            <button
              onClick={handleClearFilters}
              className="text-(--color-text-secondary) hover:text-(--color-text-primary) underline"
            >
              Limpar filtros
            </button>
          </div>
        )}

        {isError ? (
          <ErrorState
            message={getApiErrorMessage(
              error,
              'Não foi possível carregar as transações. Revise os filtros e tente novamente.',
            )}
          />
        ) : (
          <Table
            columns={columns}
            data={items}
            keyExtractor={(row) => row.id}
            loading={isLoading}
            emptyMessage="Nenhuma transação encontrada."
            striped
            sortColumn={sort.sortBy}
            sortDirection={sort.sortDirection}
            onSort={handleHeaderSort}
            onRowClick={(row) => void navigate(`/sales/${row.id}`)}
          />
        )}

        <div className="flex items-center justify-between">
          <p className="text-(--color-text-secondary) text-xs">
            {data ? (
              <>
                {data.total}{' '}
                {data.total === 1
                  ? 'transação encontrada'
                  : 'transações encontradas'}
              </>
            ) : null}
          </p>

          {totalPages > 1 && (
            <TablePagination
              page={page}
              totalPages={totalPages}
              disabled={isLoading}
              onPageChange={setPage}
            />
          )}
        </div>
      </SurfaceCard>
    </PageContainer>
  );
}
